import React, { useCallback, useEffect, useState } from "react";
import { IconButton, MenuItem, Snackbar, TextField, Tooltip } from "@material-ui/core";
import CallOutlinedIcon from "@material-ui/icons/CallOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import useStyles from "./styles";
import {
  STATUS_COLORS,
  getCrmSettingsCache,
  loadCrmSettings,
  subscribeCrmSettings,
  telHref,
  whatsappHref,
} from "./crmService";

/** CRM settings + fixed option lists, shared by every CRM screen and kept current. */
export function useCrmSettings() {
  const [state, setState] = useState(() => ({ data: getCrmSettingsCache(), error: "" }));
  useEffect(() => {
    let alive = true;
    const refresh = (maxAgeMs) =>
      loadCrmSettings({ maxAgeMs }).catch(
        () => alive && setState((s) => (s.data ? s : { data: null, error: "Could not load CRM settings." }))
      );
    const off = subscribeCrmSettings((data) => alive && setState({ data, error: "" }));
    // Opening a page re-reads settings (dialogs mounted with it share one request).
    refresh(2000);
    const onFocus = () => document.visibilityState === "visible" && refresh(5000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      off();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);
  const d = state.data;
  return {
    loading: !d && !state.error,
    error: state.error,
    settings: d?.settings || null,
    options: d?.options || null,
    inUse: d?.inUse || null,
    tz: d?.timeZone || "Asia/Kolkata",
  };
}

/** List from settings, plus `extra` values (old or imported names) not in it. */
export function withExtras(list, ...extras) {
  const out = [...(list || [])];
  extras.flat().forEach((v) => {
    if (v && !out.includes(v)) out.push(v);
  });
  return out;
}

/** Bottom-left toast with a status dot (success / error). */
export function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((message, kind = "success") => setToast({ message, kind, key: Date.now() }), []);
  const node = (
    <Snackbar
      key={toast?.key}
      open={Boolean(toast)}
      autoHideDuration={4000}
      onClose={() => setToast(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <div
        role="status"
        style={{
          background: "#fff",
          border: "1px solid #d9d9d9",
          borderRadius: 12,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 14,
          color: "#1a1a1a",
          maxWidth: 420,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flex: "0 0 8px",
            background: toast?.kind === "error" ? "#b3261e" : "#1f8a5b",
          }}
        />
        {toast?.message}
      </div>
    </Snackbar>
  );
  return [show, node];
}

export function StatusDot({ status }) {
  const classes = useStyles();
  return (
    <span className={classes.status}>
      <span className={classes.dot} style={{ background: STATUS_COLORS[status] || "#8a8a8a" }} />
      {status}
    </span>
  );
}

export const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

/** One-tap choice chips (single select). */
export function ChoiceChips({ options, value, onChange, ariaLabel }) {
  const classes = useStyles();
  return (
    <div className={classes.chips} role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const on = opt === value;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={on}
            className={`${classes.chip} ${on ? classes.chipOn : ""}`}
            onClick={() => onChange(on ? "" : opt)}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/** Call + WhatsApp icon buttons. */
export function ContactButtons({ enquiry }) {
  const classes = useStyles();
  if (!enquiry?.mobile) return null;
  const name = enquiry.parentName || "";
  return (
    <>
      <Tooltip title={`Call ${enquiry.mobile}`}>
        <IconButton
          className={classes.iconBtn}
          component="a"
          href={telHref(enquiry.mobile)}
          aria-label={`Call ${name} on ${enquiry.mobile}`}
          onClick={(e) => e.stopPropagation()}
        >
          <CallOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="WhatsApp">
        <IconButton
          className={classes.iconBtn}
          component="a"
          href={whatsappHref(enquiry.mobile, name ? `Hello ${name}, ` : "")}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`WhatsApp ${name}`}
          onClick={(e) => e.stopPropagation()}
        >
          <WhatsAppIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
}

export function PageHead({ title, subtitle, actions, back }) {
  const classes = useStyles();
  return (
    <>
      {back}
      <div className={classes.head}>
        <div>
          <h1 className={classes.h1}>{title}</h1>
          {subtitle && <div className={classes.sub}>{subtitle}</div>}
        </div>
        {actions && <div className={classes.headActions}>{actions}</div>}
      </div>
    </>
  );
}

export function EmptyState({ icon, title, text, action }) {
  const classes = useStyles();
  return (
    <div className={classes.empty}>
      {icon && <div style={{ color: "#8a8a8a" }}>{icon}</div>}
      <div className={classes.emptyTitle}>{title}</div>
      {text && <div>{text}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
const selectMenu = { MenuProps: { PaperProps: { style: { maxHeight: 260 } } } };

/** "14:30" -> { hour: "2", minute: "30", period: "PM" }; "" -> empty parts. */
function splitTime(value) {
  if (!value) return { hour: "", minute: "00", period: "AM" };
  const [h, m] = value.split(":").map(Number);
  return { hour: String(((h + 11) % 12) + 1), minute: String(m).padStart(2, "0"), period: h >= 12 ? "PM" : "AM" };
}

/**
 * Time picker as Hour / Minute / AM-PM, whatever the browser's locale. The
 * value stays "HH:mm" (24-hour) like a native time input; "" means no time.
 */
export function TimeField12({ value, onChange, label = "Time", disabled }) {
  const [parts, setParts] = useState(() => splitTime(value));
  useEffect(() => {
    setParts((p) => (value ? splitTime(value) : { ...p, hour: "" }));
  }, [value]);

  const change = (patch) => {
    const next = { ...parts, ...patch };
    setParts(next);
    if (!next.hour) return onChange("");
    const h = (Number(next.hour) % 12) + (next.period === "PM" ? 12 : 0);
    return onChange(`${String(h).padStart(2, "0")}:${next.minute}`);
  };
  const minutes = MINUTES.includes(parts.minute) ? MINUTES : [...MINUTES, parts.minute].sort();
  const common = { select: true, variant: "outlined", size: "small", disabled, InputLabelProps: { shrink: true } };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr", gap: 6 }}>
      <TextField
        {...common}
        label={label}
        value={parts.hour}
        onChange={(e) => change({ hour: e.target.value })}
        SelectProps={{ ...selectMenu, displayEmpty: true }}
      >
        <MenuItem value="">
          <span style={{ color: "#777" }}>Hour</span>
        </MenuItem>
        {HOURS_12.map((h) => (
          <MenuItem key={h} value={h}>
            {h}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        {...common}
        label="Min"
        value={parts.minute}
        onChange={(e) => change({ minute: e.target.value })}
        disabled={disabled || !parts.hour}
        SelectProps={selectMenu}
      >
        {minutes.map((m) => (
          <MenuItem key={m} value={m}>
            {m}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        {...common}
        label="AM/PM"
        value={parts.period}
        onChange={(e) => change({ period: e.target.value })}
        disabled={disabled || !parts.hour}
      >
        <MenuItem value="AM">AM</MenuItem>
        <MenuItem value="PM">PM</MenuItem>
      </TextField>
    </div>
  );
}

/**
 * Duplicate-parent check while a mobile number is typed: waits for a pause,
 * then asks `check(mobile)` ({ duplicate, message }). Returns the message, or
 * "" while the number is short, unique, or the check could not run (the save
 * still checks on the server).
 */
export function useDuplicateMobile(mobile, check, enabled = true) {
  const [message, setMessage] = useState("");
  useEffect(() => {
    setMessage("");
    const digits = String(mobile || "").replace(/\D/g, "");
    if (!enabled || digits.length < 10) return undefined;
    let live = true;
    const t = setTimeout(() => {
      check(mobile)
        .then((res) => live && setMessage(res?.duplicate ? res.message : ""))
        .catch(() => {});
    }, 400);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [mobile, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return message;
}
